import gql from 'graphql-tag';
import { apolloClient as apollo } from '@/vue-apollo';
import { store } from '@/store';

const state = () => ({
	totalNotification: 0,
	totalDocToRead: 0,
	totalDocToSign: 0,
	totalAccToRead: 0,
	totalTsToValidate: 0,
	totalPoToValidate: 0,
	totalExpToValidate: 0,
	totalTsRefused: 0,
	totalPoRefused: 0,
	totalExpRefused: 0,
	totalExpItemRefused: 0,
	totalInvoiceRefused: 0,
	totalInvoiceToValidate: 0,
	countAppVersionErrorLoaded: 0,
	requiredAppVersion: null,
	isEmptyTokenData: false
});

const getters = {
	getTotalNotification: (state) => state.totalNotification,
	getTotalDocToRead: (state) => state.totalDocToRead,
	getTotalDocToSign: (state) => state.totalDocToSign,
	getTotalAccToRead: (state) => state.totalAccToRead,
	getTotalTsToValidate: (state) => state.totalTsToValidate,
	getTotalPoToValidate: (state) => state.totalPoToValidate,
	getTotalExpToValidate: (state) => state.totalExpToValidate,
	getTotalTsRefused: (state) => state.totalTsRefused,
	getTotalPoRefused: (state) => state.totalPoRefused,
	getTotalExpRefused: (state) => state.totalExpRefused,
	getTotalExpItemRefused: (state) => state.totalExpItemRefused,
	getTotalInvoiceRefused: (state) => (store.getCurrentProjectConfig().useExpenseInvoice ? state.totalInvoiceRefused : 0),
	getTotalInvoiceToValidate: (state) => (store.getCurrentProjectConfig().useExpenseInvoice ? state.totalInvoiceToValidate : 0),
	getCountAppVersionErrorLoaded: (state) => state.countAppVersionErrorLoaded,
	getRequiredAppVersion: (state) => state.requiredAppVersion,
	getEmptyTokenData: (state) => state.isEmptyTokenData
};

const mutations = {
	SET_TOTAL_NOTIFICATION(state, total) {
		state.totalNotification = total;
	},
	SET_TOTAL_DOC_TO_READ(state, total) {
		state.totalDocToRead = total;
	},
	SET_TOTAL_ACC_TO_READ(state, total) {
		state.totalAccToRead = total;
	},
	SET_TOTAL_TS_TO_VALIDATE(state, total) {
		state.totalTsToValidate = total;
	},
	SET_TOTAL_PO_TO_VALIDATE(state, total) {
		state.totalPoToValidate = total;
	},
	SET_TOTAL_EXP_TO_VALIDATE(state, total) {
		state.totalExpToValidate = total;
	},
	SET_TOTAL_TS_REFUSED(state, total) {
		state.totalTsRefused = total;
	},
	SET_TOTAL_EXP_REFUSED(state, total) {
		state.totalExpRefused = total;
	},
	SET_TOTAL_PO_REFUSED(state, total) {
		state.totalPoRefused = total;
	},
	SET_TOTAL_EXP_ITEM_REFUSED(state, total) {
		state.totalExpItemRefused = total;
	},
	SET_TOTAL_INVOICE_REFUSED(state, total) {
		state.totalInvoiceRefused = total;
	},
	SET_TOTAL_INVOICE_TO_VALIDATE(state, total) {
		state.totalInvoiceToValidate = total;
	},
	SET_COUNT_APP_VERSION_ERROR_LOADED(state) {
		state.countAppVersionErrorLoaded++;
	},
	SET_REQUIRED_APP_VERSION(state, version) {
		state.requiredAppVersion = version;
	},
	SET_TOTAL_DOC_TO_SIGN(state, total) {
		state.totalDocToSign = total;
	},
	RESET_COUNT_APP_VERSION_ERROR_LOADED(state) {
		state.countAppVersionErrorLoaded = 0;
	},
	SET_EMPTY_TOKEN_DATA(state, isEmpty) {
		state.isEmptyTokenData = isEmpty;
	}
};

const actions = {
	async getNotifications(
		{ commit },
		projectId = 0,
		numDocToRead = false,
		numDocToSign = true,
		numAccToRead = false,
		numTsToValidate = true,
		numExpToValidate = true,
		numPoToValidate = true,
		numTsRefused = true,
		numExpRefused = true,
		numPoRefused = true,
		numExpItemRefused = true,
		numInvoiceRefused = true,
		numInvoiceToValidate = true
	) {
		const {
			data: { GetHeaderData }
		} = await apollo.query({
			query: gql`
				query (
					$projectId: ID
					$numDocToRead: Boolean
					$numDocToSign: Boolean
					$numAccToRead: Boolean
					$numTsToValidate: Boolean
					$numExpToValidate: Boolean
					$numPoToValidate: Boolean
					$numTsRefused: Boolean
					$numExpRefused: Boolean
					$numPoRefused: Boolean
					$numExpItemRefused: Boolean
					$numInvoiceRefused: Boolean
					$numInvoiceToValidate: Boolean
				) {
					GetHeaderData(
						ProjectId: $projectId
						NumDocToRead: $numDocToRead
						NumDocToSign: $numDocToSign
						NumAccToRead: $numAccToRead
						NumTsToValidate: $numTsToValidate
						NumExpToValidate: $numExpToValidate
						NumPoToValidate: $numPoToValidate
						NumTsRefused: $numTsRefused
						NumExpRefused: $numExpRefused
						NumPoRefused: $numPoRefused
						NumExpItemRefused: $numExpItemRefused
						NumInvoiceRefused: $numInvoiceRefused
						NumInvoiceToValidate: $numInvoiceToValidate
					) {
						numDocToRead
						numDocToSign
						numAccToRead
						numTsToValidate
						numPoToValidate
						numExpToValidate
						numTsRefused
						numExpRefused
						numPoRefused
						numExpItemRefused
						numInvoiceRefused
						numInvoiceToValidate
					}
				}
			`,
			variables: {
				projectId: parseInt(projectId, 10),
				numDocToRead,
				numDocToSign,
				numAccToRead,
				numTsToValidate,
				numExpToValidate,
				numPoToValidate,
				numTsRefused,
				numExpRefused,
				numPoRefused,
				numExpItemRefused,
				numInvoiceRefused,
				numInvoiceToValidate
			},
			fetchPolicy: 'no-cache'
		});

		let totalInvoiceNotification = store.getCurrentProjectConfig().useExpenseInvoice
			? GetHeaderData.numInvoiceToValidate + GetHeaderData.numInvoiceRefused
			: 0;

		commit(
			'SET_TOTAL_NOTIFICATION',
			GetHeaderData.numDocToRead +
				GetHeaderData.numDocToSign +
				GetHeaderData.numAccToRead +
				GetHeaderData.numTsToValidate +
				GetHeaderData.numPoToValidate +
				GetHeaderData.numExpToValidate +
				GetHeaderData.numTsRefused +
				GetHeaderData.numExpRefused +
				GetHeaderData.numPoRefused +
				GetHeaderData.numExpItemRefused +
				totalInvoiceNotification
		);
		commit('SET_TOTAL_DOC_TO_READ', GetHeaderData.numDocToRead);
		commit('SET_TOTAL_DOC_TO_SIGN', GetHeaderData.numDocToSign);
		commit('SET_TOTAL_ACC_TO_READ', GetHeaderData.numAccToRead);
		commit('SET_TOTAL_TS_TO_VALIDATE', GetHeaderData.numTsToValidate);
		commit('SET_TOTAL_PO_TO_VALIDATE', GetHeaderData.numPoToValidate);
		commit('SET_TOTAL_EXP_TO_VALIDATE', GetHeaderData.numExpToValidate);
		commit('SET_TOTAL_TS_REFUSED', GetHeaderData.numTsRefused);
		commit('SET_TOTAL_EXP_REFUSED', GetHeaderData.numExpRefused);
		commit('SET_TOTAL_PO_REFUSED', GetHeaderData.numPoRefused);
		commit('SET_TOTAL_EXP_ITEM_REFUSED', GetHeaderData.numExpItemRefused);
		commit('SET_TOTAL_INVOICE_REFUSED', GetHeaderData.numInvoiceRefused);
		commit('SET_TOTAL_INVOICE_TO_VALIDATE', GetHeaderData.numInvoiceToValidate);
	},
	setErrorAppVersion({ commit }, version = null) {
		commit('SET_REQUIRED_APP_VERSION', version);
	},
	setCountAppVersionErrorLoaded({ commit }) {
		commit('SET_COUNT_APP_VERSION_ERROR_LOADED');
	},
	resetCountAppVersionErrorLoaded({ commit }) {
		commit('RESET_COUNT_APP_VERSION_ERROR_LOADED');
	},
	setEmptyTokenData({ commit }, isEmpty) {
		commit('SET_EMPTY_TOKEN_DATA', isEmpty);
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};
